import { useState } from 'react';
import { LayerPaths, buildLayerUrl, useMapStore, useUIStore } from '@uofu-uss-gis/ui-react';
import { useTheme } from '@mui/material/styles';
import { useAuthStore } from '../../store/authStore';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Point from '@arcgis/core/geometry/Point';
import { decodeDays, getBuildingIcon, formatDate, normalizeBuildingCode } from '../../utils/classUtils';
import { classMapDirectionsController } from '../../services/directionsController';
import useZoomToClassRoom from './useZoomToClassRoom';
import AddToCalendarModal from './AddToCalendarModal';

const MyClassDetail = () => {
  const { payload: myClasses } = useAuthStore();
  const { selectedClassIndex, setMyClassesOpen, setMyClassesDetailOpen, setSidebarOpen } = useUIStore();
  const { mapView, restEndpoint } = useMapStore();
  const theme = useTheme();
  const { zoomToClassRoom } = useZoomToClassRoom();
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);

  if (selectedClassIndex === null || !myClasses?.[selectedClassIndex]) {
    return null;
  }

  const { attributes } = myClasses[selectedClassIndex];
  const courseShort = attributes.UU_COURSE_TITLE.split(' - ')[0];
  const courseLong = attributes.UU_COURSE_TITLE.split(' - ')[1];
  const bldgCd = attributes.BLDG_CD.trim();
  const location = attributes.UU_CRSE_LOC;
  const roomNum = location?.split(' ').pop() || 'N/A';
  const days = decodeDays(attributes.UU_CRSE_DAYS);
  const startEnd = attributes.UU_CRS_STRT_END_TM;
  const instructors = attributes.UU_INSTR_LIST;
  const durationStart = formatDate(attributes.START_DT);
  const durationEnd = formatDate(attributes.END_DT);
  const imgPath = getBuildingIcon(bldgCd, selectedClassIndex, theme.palette.mode === 'dark');

  const handleBack = () => {
    setMyClassesDetailOpen(false);
  };

  const handleViewOnMap = () => {
    if (!mapView || !restEndpoint) {
      return;
    }

    // Try to refine to an indoor room/floor, but do not block building zoom.
    void zoomToClassRoom({
      buildingCode: bldgCd,
      roomNumber: roomNum,
    }).catch((error) => {
      console.error('Error preparing room-level class zoom:', error);
    });

    const normalizedBldgCd = normalizeBuildingCode(bldgCd);
    const structureFL = new FeatureLayer({
      url: buildLayerUrl(restEndpoint, LayerPaths.structures)
    });

    const query = structureFL.createQuery();
    query.where = `ABBREVIATION = '${normalizedBldgCd.replace(/'/g, "''")}'`;
    query.outFields = ['ABBREVIATION', 'OBJECTID'];
    query.returnGeometry = true;

    structureFL.queryFeatures(query).then((results) => {
      const feature = results.features[0];
      const geometry = feature?.geometry;

      if (!geometry) {
        return;
      }

      const target = (geometry as any).extent?.expand(2) ?? geometry;
      void mapView.goTo({
        target,
        zoom: Math.max(mapView.zoom ?? 0, 18)
    });
      mapView.popup?.close();
      setMyClassesDetailOpen(false);
      setMyClassesOpen(false);
    }).catch((error) => {
      console.error('Error locating class building on map:', error);
    });
  };

  const getCurrentPosition = () => new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000
    });
  });

  const handleDirections = () => {
    if (!mapView || !restEndpoint) {
      classMapDirectionsController.setErrorMessage('Map is still loading. Please try again in a moment.');
      return;
    }

    if (!navigator.geolocation) {
      classMapDirectionsController.setErrorMessage('Current location is not available in this browser.');
      return;
    }

    const normalizedBldgCd = normalizeBuildingCode(bldgCd);
    const structureFL = new FeatureLayer({
      url: buildLayerUrl(restEndpoint, LayerPaths.structures)
    });

    const query = structureFL.createQuery();
    query.where = `ABBREVIATION = '${normalizedBldgCd.replace(/'/g, "''")}'`;
    query.outFields = ['OBJECTID', 'formal_name', 'informal_name', 'abbreviation', 'building_number'];
    query.returnGeometry = true;

    Promise.all([getCurrentPosition(), structureFL.queryFeatures(query)]).then(([position, results]) => {
      const feature = results.features[0];
      const geometry = feature?.geometry;
      const centroid = (geometry as any)?.extent?.center;

      if (!feature || !geometry || !centroid) {
        classMapDirectionsController.setErrorMessage('Could not find this class building for directions.');
        return;
      }

      const originPoint = new Point({
        x: position.coords.longitude,
        y: position.coords.latitude,
        spatialReference: { wkid: 4326 }
      });
      const destinationPoint = new Point({
        x: centroid.x,
        y: centroid.y,
        spatialReference: geometry.spatialReference
      });
      const formalName = feature.attributes.formal_name;
      const informalName = feature.attributes.informal_name;
      const abbreviation = feature.attributes.abbreviation ?? normalizedBldgCd;
      const buildingNumber = feature.attributes.building_number;
      const destinationLabel = formalName || informalName || abbreviation;

      classMapDirectionsController.clear();
      classMapDirectionsController.selectSuggestion('from', {
        id: 'current-location',
        label: 'Current Location',
        detail: 'Your current location',
        point: originPoint
      });
      classMapDirectionsController.selectSuggestion('to', {
        id: String(feature.attributes.OBJECTID ?? abbreviation),
        label: destinationLabel,
        detail: [abbreviation, buildingNumber ? `Building ${buildingNumber}` : undefined].filter(Boolean).join(' • '),
        point: destinationPoint
      });
      classMapDirectionsController.setActiveInput(null);
      setSidebarOpen(true);
      mapView.popup?.close();
      setMyClassesDetailOpen(false);
      setMyClassesOpen(false);
    }).catch((error) => {
      console.error('Error preparing class directions:', error);
      classMapDirectionsController.setErrorMessage('Could not use your current location for directions.');
      setSidebarOpen(true);
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b bg-u-red text-white">
        <button
          type="button"
          onClick={handleBack}
          className="mb-2 text-white hover:opacity-75"
          aria-label="Back to My Classes list"
        >
          ← Back
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12">
            <img
              src={`/app/resources/images/${imgPath}.svg`}
              alt={imgPath}
              width={48}
              height={48}
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <p className="font-bold text-sm">{courseShort}</p>
            <p className="text-xs" style={{ color: theme.palette.text.secondary }}>{courseLong}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase" style={{ color: theme.palette.text.secondary }}>Building</p>
            <p className="text-sm">{bldgCd}</p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase" style={{ color: theme.palette.text.secondary }}>Room</p>
            <p className="text-sm">{roomNum}</p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase" style={{ color: theme.palette.text.secondary }}>Instructor</p>
            <p className="text-sm">{instructors}</p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase" style={{ color: theme.palette.text.secondary }}>Schedule</p>
            <p className="text-sm">{days} - {startEnd}</p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase" style={{ color: theme.palette.text.secondary }}>Course Duration</p>
            <p className="text-sm">{durationStart} - {durationEnd}</p>
          </div>
        </div>

        {!['TBA', '', 'CANVAS', 'ONLN', 'SANDY', 'ST. GEORGE'].includes(bldgCd) && (
          <div className="mt-4">
            <button type="button" onClick={handleViewOnMap} className="w-full mb-2 py-2 px-4 bg-u-red text-white rounded hover:bg-red-700">
              View on Map
            </button>
            <button type="button" onClick={handleDirections} className="w-full mb-2 py-2 px-4 bg-u-red text-white rounded hover:bg-red-700">
              Directions
            </button>
          </div>
        )}

        <div className="mt-4">
          <button
            type="button"
            onClick={() => setIsCalendarModalOpen(true)}
            className="w-full py-2 px-4 bg-u-red text-white rounded hover:bg-red-700"
          >
            Add to Calendar
          </button>
        </div>
      </div>

      <AddToCalendarModal
        open={isCalendarModalOpen}
        classInfo={myClasses[selectedClassIndex]}
        onClose={() => setIsCalendarModalOpen(false)}
      />
    </div>
  );
};

export default MyClassDetail;
